import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  ListItemAvatar,
  Avatar,
  Grid,
} from '@mui/material';
import { CloudUpload, Delete } from '@mui/icons-material';
import { createShopOffer, getShopOffers, deleteShopOffer } from '../services/shopOffers';

const ShopOfferFormDialog = ({ open, onClose, shopId }) => {
  const [offerDescription, setOfferDescription] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [imageFile, setImageFile] = useState(null);
  const [offers, setOffers] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  useEffect(() => {
    if (open && shopId) {
      fetchOffers();
    }
  }, [open, shopId]);

  const fetchOffers = async () => {
    try {
      const existingOffers = await getShopOffers(shopId);
      setOffers(existingOffers);
    } catch (error) {
      showSnackbar('Failed to fetch offers.', 'error');
    }
  };

  const handleFileChange = (event) => {
    setImageFile(event.target.files[0]);
  };

  const handleUpload = async () => {
    if (!offerDescription || !imageFile) {
      showSnackbar('Please provide an offer description and an image.', 'error');
      return;
    }

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      showSnackbar('Start date cannot be after end date.', 'error');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    try {
      await createShopOffer(shopId, offerDescription, imageFile, startDate, endDate, (progress) => {
        setUploadProgress(progress);
      });
      showSnackbar('Offer created successfully!');
      fetchOffers(); // Refresh the offers list
      setOfferDescription('');
      setImageFile(null);
      setStartDate('');
      setEndDate('');
    } catch (error) {
      showSnackbar('Failed to create offer.', 'error');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (offerId) => {
    try {
      await deleteShopOffer(offerId);
      showSnackbar('Offer deleted successfully!');
      fetchOffers(); // Refresh the offers list
    } catch (error) {
      showSnackbar('Failed to delete offer.', 'error');
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Typography variant="h6">Manage Shop Offers</Typography>
      </DialogTitle>
      <DialogContent>
        <Box mb={2}>
            <Typography variant="subtitle1">Create New Offer</Typography>
            <TextField
              autoFocus
              margin="dense"
              id="offer_description"
              label="Offer Description"
              type="text"
                        fullWidth
                        variant="outlined"
                                  value={offerDescription}
                                  onChange={(e) => setOfferDescription(e.target.value)}
                                />
                                <Grid container spacing={2}>
                                  <Grid item xs={6}>
                                    <TextField
                                      margin="dense"
                                      id="start-date"
                                      label="Start Date"
                                      type="date"
                                      fullWidth
                                      variant="outlined"
                                      value={startDate}
                                      onChange={(e) => setStartDate(e.target.value)}
                                      InputLabelProps={{
                                        shrink: true,
                                      }}
                                    />
                                  </Grid>
                                  <Grid item xs={6}>
                                    <TextField
                                      margin="dense"
                                      id="end-date"
                                      label="End Date"
                                      type="date"
                                      fullWidth
                                      variant="outlined"
                                      value={endDate}
                                      onChange={(e) => setEndDate(e.target.value)}
                                      InputLabelProps={{
                                        shrink: true,
                                      }}
                                    />
                                  </Grid>
                                </Grid>            <Box mt={2}>
              <Button
                variant="contained"
                component="label"
                startIcon={<CloudUpload />}
              >
                Upload Image
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileChange}
                />
              </Button>
              {imageFile && (
                <Typography variant="body2" mt={1}>
                  {imageFile.name}
                </Typography>
              )}
            </Box>
            {uploading && (
              <Box sx={{ width: '100%', mt: 2 }}>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}
        </Box>
        <Box mt={4}>
            {offers.length > 0 && <Typography variant="subtitle1">Existing Offers</Typography>}
            <List>
                {offers.map((offer) => (
                    <ListItem key={offer.id} divider>
                        <ListItemAvatar>
                            <Avatar
                                src={offer.image}
                                sx={{ width: 60, height: 60, mr: 2 }}
                                variant="rounded"
                            />
                        </ListItemAvatar>
                        <ListItemText 
                            primary={offer.offer_description} 
                            secondary={
                                offer.start_date && offer.end_date 
                                ? `Start: ${offer.start_date} | End: ${offer.end_date}` 
                                : ''
                            }
                        />
                        <ListItemSecondaryAction>
                            <IconButton edge="end" aria-label="delete" onClick={() => handleDelete(offer.id)}>
                                <Delete />
                            </IconButton>
                        </ListItemSecondaryAction>
                    </ListItem>
                ))}
            </List>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleUpload} variant="contained" disabled={uploading}>
          {uploading ? <CircularProgress size={24} /> : 'Create'}
        </Button>
      </DialogActions>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default ShopOfferFormDialog;
